import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import {
  editProfileRequest,
  changePasswordRequest,
  clearProfileMessages,
  verifyEmailRequest,
  getProfileRequest,
} from "../../redux/actions/profileActions";
import { updateLogoRequest } from "../../redux/actions/logoActions";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { BiEdit } from "react-icons/bi";
import { MdVerified } from "react-icons/md";
import { FaCamera, FaPhoneAlt, FaEnvelope, FaBuilding, FaGlobe, FaDollarSign, FaChartBar, FaCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import "./ReactSelect.css";
import ChangeMailModal from "../../components/modal/ChangeMailModal";

const Profile = () => {
  const { user } = useSelector((state) => state.auth);
  // Profile form
  const {
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    register: registerProfile,
    setValue: setProfileValue,
    reset: resetProfile,
    control: controlProfile,
    watch: watchProfile,
  } = useForm({});
  const id = user?.itSalesPartner?.id;
  // Password form
  const {
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    register: registerPassword,
    watch: watchPassword,
  } = useForm({});

  const newPassword = watchPassword("newPassword");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const {
    loading: profileLoading,
    error,
    success,
  } = useSelector((state) => state.profile);
  const { loading: oemLoading, data: profileData } = useSelector(
    (state) => state.profile,
  );
  const { uploading: logoUploading, response: logoResponse } = useSelector(
    (state) => state.logo,
  );
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryCodeMap, setCountryCodeMap] = useState({});
  const [selectedCountryCode, setSelectedCountryCode] = useState("US");
  const [countryCodes, setCountryCodes] = useState([]);
  const [flagLink, setFlagLink] = useState("us");
  const [countryCode, setCountryCode] = useState("+1");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const profilePicInputRef = useRef(null);

  const formatPhone = (num) => {
    const digits = num.replace(/\D/g, "");

    if (digits.length <= 6) return digits; // fallback

    const first = digits.slice(0, 3);
    const second = digits.slice(3, 6);
    const rest = digits.slice(6);

    return `${first} ${second} ${rest}`;
  };
  useEffect(() => {
    if (id && user) {
      dispatch(getProfileRequest(id));
    }
  }, [dispatch, id, user]);

  // Listen for logo upload success
  useEffect(() => {
    if (logoResponse?.status === 200) {
      dispatch(getProfileRequest(id));
    }
  }, [logoResponse, dispatch, id]);

  // Set form values when profile data loads
  useEffect(() => {
    if (profileData) {
      setProfileValue("firstName", profileData.firstName || "");
      setProfileValue("lastName", profileData.lastName || "");
      setProfileValue("currency", profileData.currency || "");
      setProfileValue("country", profileData.country || "");

      // Set country code from profile data
      if (profileData.countryCode) {
        setCountryCode(profileData.countryCode);
      }

      // Handle contact number - extract number without country code
      if (profileData.contactNumber && profileData.countryCode) {
        const numberWithoutCode = profileData.contactNumber.replace(
          profileData.countryCode,
          "",
        );
        setProfileValue("contactNumber", numberWithoutCode);
      } else {
        setProfileValue("contactNumber", profileData.contactNumber || "");
      }

      // Set flag from flagLink if available
      if (profileData.flagLink) {
        // Extract country code from flag URL (e.g., "https://flagcdn.com/io.svg" -> "io")
        const flagCode = profileData.flagLink
          .split("/")
          .pop()
          .replace(".svg", "");
        setFlagLink(flagCode);
        setSelectedCountryCode(flagCode.toUpperCase());
      }
    }
  }, [profileData, setProfileValue]);

  const onProfileSubmit = (data) => {
    setIsProfileSubmitting(true);
    const fullContactNumber = data.contactNumber
      ? `${countryCode}${data.contactNumber}`
      : "";

    const profileData = {
      firstName: data.firstName,
      lastName: data.lastName || "", // Ensure empty string when cleared
      country: data.country,
      currency: data.currency,
      contactNumber: fullContactNumber,
    };
    dispatch(editProfileRequest(profileData));
  };

  const onPasswordSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      return;
    }

    setIsPasswordSubmitting(true);
    const passwordData = {
      _id: id,
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    };
    dispatch(changePasswordRequest(passwordData));
  };

  useEffect(() => {
    if (success) {
      const shouldRefetchProfile = isProfileSubmitting;
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      // Only fetch profile data if profile was updated, not for password changes
      if (shouldRefetchProfile && id) {
        dispatch(getProfileRequest(id));
      }
      dispatch(clearProfileMessages());
    }
    if (error) {
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      dispatch(clearProfileMessages());
    }
  }, [success, error, dispatch, id, isProfileSubmitting]);

  const loadCountryOptions = async (inputValue) => {
    try {
      const response = inputValue
        ? await fetch(
            `https://restcountries.com/v3.1/name/${inputValue}?fields=name,currencies,cca2,cca3,ccn3,idd`,
          )
        : await fetch(
            "https://restcountries.com/v3.1/all?fields=name,currencies,cca2,cca3,ccn3,idd",
          );
      const data = await response.json();
      return data.slice(0, 10).map((country) => {
        const currency = Object.keys(country.currencies || {})[0] || "USD";
        return {
          value: country.name.common,
          label: country.name.common,
          isoCode: country.cca2,
          currency: currency,
          countryCode: country.idd.root
            ? country.idd.root +
              (country.idd.suffixes?.length
                ? country.name.common === "United States"
                  ? ""
                  : country?.idd?.suffixes?.[0]
                : "")
            : "",
        };
      });
    } catch (error) {
      return [];
    }
  };

  const handleCountryChange = (selectedOption, { action }) => {
    if (action === "clear") {
      setProfileValue("country", "");
      setProfileValue("currency", "");
      setFlagLink("us");
      setCountryCode("+1");
      setSelectedCountryCode("US");
      return null;
    }
    setProfileValue("country", selectedOption?.value || "");
    setProfileValue("currency", selectedOption?.currency || "USD");

    // Update flag and country code for contact number
    setFlagLink(selectedOption?.isoCode || "us");
    setCountryCode(selectedOption?.countryCode || "+1");
    setSelectedCountryCode(selectedOption?.isoCode || "US");

    return selectedOption;
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,cca2,idd",
        );
        const data = await response.json();
        const countryList = data
          .map((country) => ({
            name: country.name.common,
            code: country.cca2,
            dialCode: country.idd?.root
              ? `${country.idd.root}${
                  country.name.common === "United States"
                    ? ""
                    : country.idd.suffixes?.[0] || ""
                }`
              : "",
          }))
          .filter((country) => country.dialCode)
          .sort((a, b) => a.name.localeCompare(b.name));

        setCountries(countryList);
        setCountryCodes(countryList);

        // Create mapping from code to name
        const codeToName = {};
        countryList.forEach((country) => {
          codeToName[country.code] = country.name;
        });
        setCountryCodeMap(codeToName);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };
    fetchCountries();
  }, []);
  if (oemLoading && !profileData) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: "20px",
              }}
            ></div>
            <p style={{ color: "#7f8c8d", fontSize: "16px" }}>
              Loading profile...
            </p>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2">
        <div className="scroll-container">
          <div className="input-form-section">
            <div className="row g-2">
              {/* Left Side - Profile Details Display */}
              <div className="col-3">
                <div className="page-card1 h-100 p-4">
                  {/* Profile Picture & Basic Info */}
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "30px",
                      position: "relative",
                      display: "inline-block",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        margin: "0 auto",
                      }}
                    >
                      {profileData?.logo ? (
                        <img
                          src={profileData.logo}
                          alt="Profile"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            objectFit: "scale-down",
                            border: "#737EE2",
                            background:
                              "radial-gradient(circle, rgb(227, 242, 253), black)",
                          }}
                        />
                      ) : profileData?.firstName?.[0] ||
                        profileData?.lastName?.[0] ? (
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, rgb(37, 99, 235), rgb(67, 56, 202))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 15px",
                            fontSize: "32px",
                            color: "#fff",
                          }}
                        >
                          {profileData?.firstName?.[0]}
                          {profileData?.lastName?.[0]}
                        </div>
                      ) : (
                        <img
                          src="https://via.placeholder.com/80x80/2563eb/ffffff?text=👤"
                          alt="Default Profile"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid rgb(37, 99, 235)",
                            background: "#f9fafb",
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => profilePicInputRef.current?.click()}
                        style={{
                          position: "absolute",
                          bottom: "0",
                          right: "0",
                          background: "#737EE2",
                          color: "white",
                          border: "2px solid white",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "0",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "rgb(67, 56, 202)";
                          e.target.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "rgb(37, 99, 235)";
                          e.target.style.transform = "scale(1)";
                        }}
                        title="Change Profile Picture"
                      >
                        <FaCamera />
                      </button>
                      <input
                        type="file"
                        ref={profilePicInputRef}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          dispatch(
                            updateLogoRequest({
                              file,
                              _id: profileData?._id,
                            })
                          );
                        }}
                        accept="image/*"
                        style={{ display: "none" }}
                      />
                    </div>
                    <h4
                      style={{
                        margin: "0",
                        color: "#1f2937",
                        fontWeight: "600",
                      }}
                    >
                      {profileData?.firstName} {profileData?.lastName}
                    </h4>
                    <p
                      style={{
                        color: "#6b7280",
                        fontSize: "0.9rem",
                      }}
                    >
                      {profileData?.companyName}
                    </p>
                  </div>

                  {/* About Section */}
                  <div style={{ marginBottom: "25px" }}>
                    <h6
                      style={{
                        color: "#1f2937",
                        marginBottom: "15px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        borderBottom: "1px solid #f3f4f6",
                        paddingBottom: "10px",
                      }}
                    >
                      About
                    </h6>
                    <div style={{ marginBottom: "10px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaPhoneAlt style={{ marginRight: "5px" }} /> Phone:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.contactNumber
                          ? (() => {
                              const countryCode = profileData.countryCode || "";
                              const number = profileData.contactNumber;

                              if (countryCode && number) {
                                // Format based on country code
                                if (
                                  countryCode === "+1" &&
                                  number.length === 10
                                ) {
                                  return `(${countryCode}) ${number.slice(
                                    0,
                                    3,
                                  )} ${number.slice(3, 6)} ${number.slice(6)}`;
                                } else {
                                  return `(${countryCode}) ${number}`;
                                }
                              }
                              return number;
                            })()
                          : "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "10px", position: "relative" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaEnvelope style={{ marginRight: "5px" }} /> Email:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.email || "N/A"}
                      </span>
                      <span
                        className="position-absolute"
                        style={{
                          top: "-12%",
                          fontSize: "18px",
                          color: "#10b981",
                          cursor: "pointer",
                        }}
                        title="Email Verified"
                      >
                        <MdVerified />
                      </span>
                    </div>
                  </div>

                  {/* Company Details */}
                  <div style={{ marginBottom: "25px" }}>
                    <h6
                      style={{
                        color: "#1f2937",
                        marginBottom: "15px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        borderBottom: "1px solid #f3f4f6",
                        paddingBottom: "10px",
                      }}
                    >
                      Company Details
                    </h6>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaBuilding style={{ marginRight: "5px" }} /> Company:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.companyName || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaGlobe style={{ marginRight: "5px" }} /> Country:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.country || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaDollarSign style={{ marginRight: "5px" }} /> Currency:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.currency || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaChartBar style={{ marginRight: "5px" }} /> Almata Share:
                      </span>
                      <span
                        className="ms-2"
                        style={{ fontSize: "12px", color: "#374151" }}
                      >
                        {(() => {
                          const description =
                            profileData?.almataSharedDiscription || "N/A";
                          if (description === "N/A") return description;
                          const words = description.split(" ");
                          const isLong = words.length > 25;
                          const truncated = words.slice(0, 25).join(" ");
                          return (
                            <>
                              {isLong ? truncated + "..." : description}
                              {isLong && (
                                <span
                                  style={{
                                    color: "#007bff",
                                    cursor: "pointer",
                                    marginLeft: "5px",
                                  }}
                                  onClick={() => setShowDescriptionModal(true)}
                                >
                                  more
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        <FaCalendarAlt style={{ marginRight: "5px" }} /> Created:
                      </span>
                      <span
                        className="ms-2"
                        style={{ color: "#374151", fontSize: "0.9rem" }}
                      >
                        {profileData?.createdAt
                          ? new Date(profileData.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Edit Forms */}
              <div className="col-9" style={{ paddingLeft: "10px" }}>
                <div className="page-card1 p-4 mb-3">
                  {/* Profile Information Form */}
                  <div style={{ marginBottom: "30px" }}>
                    <h6
                      style={{
                        borderBottom: "1px solid #ddd",
                        paddingBottom: "10px",
                        marginBottom: "20px",
                      }}
                    >
                      Edit Profile Information
                    </h6>
                    <form
                      onSubmit={handleProfileSubmit(onProfileSubmit)}
                      className="input-form"
                    >
                      <div className="row">
                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>
                              First Name <span className="text-danger">*</span>
                            </label>
                            <input
                              {...registerProfile("firstName", {
                                required: "This field is required",
                                validate: {
                                  notOnlySpaces: (value) =>
                                    value.trim() !== "" ||
                                    "This field is required",
                                  noSpecialChars: (value) =>
                                    /^[A-Za-z\s]+$/.test(value) ||
                                    "Only letters are allowed",
                                },
                              })}
                              className="form-input"
                              placeholder="Enter first name"
                            />
                            {profileErrors.firstName && (
                              <span className="error-text">
                                {profileErrors.firstName.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>Last Name</label>
                            <input
                              {...registerProfile("lastName", {
                                validate: {
                                  notOnlySpaces: (value) =>
                                    !value ||
                                    value.trim() !== "" ||
                                    "This field is required",
                                  noSpecialChars: (value) =>
                                    !value ||
                                    /^[A-Za-z\s]+$/.test(value) ||
                                    "Only letters are allowed",
                                },
                              })}
                              className="form-input"
                              placeholder="Enter last name"
                            />
                            {profileErrors.lastName && (
                              <span className="error-text">
                                {profileErrors.lastName.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>Currency</label>
                            <input
                              {...registerProfile("currency")}
                              className="form-input"
                              placeholder="Currency will be set automatically"
                              readOnly
                              style={{
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label>
                              Country <span className="text-danger">*</span>
                            </label>
                            <Controller
                              name="country"
                              control={controlProfile}
                              rules={{ required: "Country is required" }}
                              render={({
                                field: { onChange, value, ...field },
                              }) => (
                                <AsyncSelect
                                  {...field}
                                  value={
                                    value
                                      ? {
                                          value,
                                          label: `${value} (${
                                            watchProfile("currency") || "USD"
                                          })`,
                                        }
                                      : null
                                  }
                                  loadOptions={loadCountryOptions}
                                  onChange={(selectedOption, action) => {
                                    setFlagLink(selectedOption?.isoCode || "");
                                    setCountryCode(
                                      selectedOption?.countryCode || "",
                                    );
                                    setSelectedCountryCode(
                                      selectedOption?.isoCode || "US",
                                    );
                                    const result = handleCountryChange(
                                      selectedOption,
                                      action,
                                    );
                                    onChange(selectedOption?.value || "");
                                  }}
                                  placeholder="Search country..."
                                  className={
                                    profileErrors.country ? "error" : ""
                                  }
                                  classNamePrefix="react-select"
                                  isClearable
                                  defaultOptions
                                  noOptionsMessage={() => "No countries found"}
                                  loadingMessage={() => "Loading countries..."}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      minHeight: "30px",
                                      height: "30px",
                                      border: profileErrors.country
                                        ? "1px solid #dc3545"
                                        : "1px solid #ddd",
                                      borderRadius: "4px",
                                      "&:hover": {
                                        border: profileErrors.country
                                          ? "1px solid #dc3545"
                                          : "1px solid #ddd",
                                      },
                                    }),
                                  }}
                                />
                              )}
                            />
                            <input
                              {...registerProfile("currency")}
                              type="hidden"
                            />
                            {profileErrors.country && (
                              <span className="error-text">
                                {profileErrors.country.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div className="form-group">
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              Email Address
                              <BiEdit
                                style={{
                                  fontSize: "16px",
                                  color: "#007bff",
                                  cursor: "pointer",
                                }}
                                onClick={() => setShowEmailModal(true)}
                                title="Change Email"
                              />
                            </label>
                            <input
                              value={profileData?.email || ""}
                              className="form-input"
                              placeholder="Email address"
                              readOnly
                              style={{
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-6 mb-3">
                          <div
                            className="form-group"
                            style={{ position: "relative" }}
                          >
                            <label>Contact Number</label>
                            <div style={{ position: "relative" }}>
                              {selectedCountryCode && (
                                <div
                                  className="position-absolute d-flex align-items-center"
                                  style={{
                                    left: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    zIndex: 0,
                                    borderRight: "1px solid #ddd",
                                    paddingRight: "8px",
                                    height: "24px",
                                    gap: "4px",
                                    background: "#fff",
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/${flagLink?.toLowerCase()}.svg`}
                                    alt="flag"
                                    width={20}
                                    height={14}
                                    style={{ objectFit: "cover" }}
                                  />
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    ({countryCode})
                                  </span>
                                </div>
                              )}
                              <input
                                {...registerProfile("contactNumber", {
                                  validate: {
                                    validNumber: (value) => {
                                      if (!value) return true;
                                      return (
                                        /^[0-9+\s\-\(\)]+$/.test(value) ||
                                        "Invalid contact number format"
                                      );
                                    },
                                  },
                                })}
                                type="tel"
                                className="form-input"
                                placeholder="Enter contact number"
                                maxLength="15"
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(
                                    /[^0-9+\s\-\(\)]/g,
                                    "",
                                  );
                                }}
                                style={{
                                  paddingLeft: selectedCountryCode
                                    ? "85px"
                                    : "12px",
                                  height: "30px",
                                  width: "100%",
                                }}
                              />
                            </div>
                            {profileErrors.contactNumber && (
                              <span className="error-text">
                                {profileErrors.contactNumber.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="form-actions mt-3">
                        <button
                          type="submit"
                          className="add-btn"
                          disabled={isProfileSubmitting}
                        >
                          {isProfileSubmitting
                            ? "Updating..."
                            : "Update Profile"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                {/* Password Change Form */}
                <div className="page-card1 p-4">
                  <h6
                    style={{
                      borderBottom: "1px solid #ddd",
                      paddingBottom: "10px",
                      marginBottom: "20px",
                    }}
                  >
                    Change Password
                  </h6>
                  <form
                    onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                    className="input-form"
                  >
                    <div className="row">
                      <div className="col-12 mb-3">
                        <div className="form-group position-relative">
                          <label>
                            Current Password{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type={showCurrentPassword ? "text" : "password"}
                            {...registerPassword("currentPassword", {
                              required: "Current password is required",
                              validate: (value) =>
                                !/\s/.test(value) ||
                                "Password cannot contain spaces",
                            })}
                            className="form-input"
                            placeholder="Enter current password"
                            onKeyDown={(e) => {
                              if (e.key === " ") {
                                e.preventDefault();
                              }
                            }}
                          />

                          {/* Toggle button */}
                          <div
                            className="position-absolute cursor-pointer"
                            style={{
                              bottom: passwordErrors.currentPassword
                                ? "30px"
                                : "8px",
                              right: "13px",
                              color: "#666",
                            }}
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                          >
                            {showCurrentPassword ? <LuEyeOff /> : <LuEye />}
                          </div>

                          {passwordErrors.currentPassword && (
                            <span className="error-text">
                              {passwordErrors.currentPassword.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <div className="form-group position-relative">
                          <label>
                            New Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type={showNewPassword ? "text" : "password"}
                            {...registerPassword("newPassword", {
                              required: "New password is required",
                              minLength: {
                                value: 8,
                                message:
                                  "Password must be at least 8 characters",
                              },
                              validate: {
                                noSpaces: (value) =>
                                  !/\s/.test(value) ||
                                  "Password cannot contain spaces",
                                hasUpperCase: (value) =>
                                  /[A-Z]/.test(value) ||
                                  "Password must contain at least one uppercase letter",
                                hasLowerCase: (value) =>
                                  /[a-z]/.test(value) ||
                                  "Password must contain at least one lowercase letter",
                                hasNumber: (value) =>
                                  /[0-9]/.test(value) ||
                                  "Password must contain at least one number",
                                hasSpecialChar: (value) =>
                                  /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                                  "Password must contain at least one special character",
                              },
                            })}
                            className="form-input"
                            placeholder="Enter new password"
                            onKeyDown={(e) => {
                              if (e.key === " ") {
                                e.preventDefault();
                              }
                            }}
                          />

                          {/* Toggle button */}
                          <div
                            className="position-absolute cursor-pointer"
                            style={{
                              bottom: passwordErrors.newPassword
                                ? "30px"
                                : "8px",
                              right: "13px",
                              color: "#666",
                            }}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? <LuEyeOff /> : <LuEye />}
                          </div>

                          {passwordErrors.newPassword && (
                            <span className="error-text">
                              {passwordErrors.newPassword.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-6 mb-3">
                        <div className="form-group position-relative">
                          <label>
                            Confirm Password{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            {...registerPassword("confirmPassword", {
                              required: "Confirm password is required",
                              validate: {
                                match: (value) =>
                                  value === newPassword ||
                                  "Passwords don't match",
                                noSpaces: (value) =>
                                  !/\s/.test(value) ||
                                  "Password cannot contain spaces",
                              },
                            })}
                            className="form-input"
                            placeholder="Confirm new password"
                            onKeyDown={(e) => {
                              if (e.key === " ") {
                                e.preventDefault();
                              }
                            }}
                          />

                          {/* Toggle button */}
                          <div
                            className="position-absolute cursor-pointer"
                            style={{
                              bottom: passwordErrors.confirmPassword
                                ? "30px"
                                : "8px",
                              right: "13px",
                              color: "#666",
                            }}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? <LuEyeOff /> : <LuEye />}
                          </div>

                          {passwordErrors.confirmPassword && (
                            <span className="error-text">
                              {passwordErrors.confirmPassword.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="form-actions mt-3">
                      <button
                        type="submit"
                        className="add-btn"
                        disabled={isPasswordSubmitting}
                      >
                        {isPasswordSubmitting
                          ? "Changing..."
                          : "Change Password"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChangeMailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        currentEmail={profileData?.email}
        itSalesId={id}
      />

      {/* Description Modal */}
      {showDescriptionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowDescriptionModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "500px",
              height: "250px",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 15px",
                borderBottom: "1px solid #ddd",
              }}
            >
              <h5 style={{ margin: 0, color: "#2c3e50" }}>
                Almata Share Description
              </h5>
              <span
                onClick={() => setShowDescriptionModal(false)}
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#666",
                  fontWeight: "bold",
                }}
              >
                ×
              </span>
            </div>
            <div
              style={{
                padding: "15px",
                overflow: "auto",
                flex: 1,
              }}
            >
              <p style={{ color: "#2c3e50", lineHeight: "1.5", margin: 0 }}>
                {profileData?.almataSharedDiscription ||
                  "No description available"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
